/**
 * CR428: Hook wrapping get_deal_card_render_payload RPC.
 * CR317: Simplified — RPC now returns hoisted, render-ready deal_json.
 *        No frontend hoisting logic; pass-through only.
 * CR433: Normalizes deal_json through buildRenderableDeal to guarantee id.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { buildRenderableDeal } from "@/lib/deals/buildRenderableDeal";
import { supabase } from "@/integrations/supabase/client";
import { hydrateDealFlyerIfMissing } from "@/lib/deals/fetchFlyerSnippetForDeal";

// ================================================================
// TYPES
// ================================================================

export interface RenderPayloadMeta {
  environment: string;
  deal_id: string | null;
  deal_type: string | null;
  dedupe_key_present: boolean;
  computed_value_source: "stored" | "derived_pct" | "derived_amount" | "missing";
  missing_fields: string[];
  source_view: string;
  updated_at: string | null;
  found: boolean;
  error?: string;
  /** CR316A: RPC contract version marker */
  contract_version?: string;
  /** CR316A: Whether value/date fields were hoisted to top-level by the RPC */
  hoisted?: boolean;
}

export interface UseDealCardRenderPayloadResult {
  deal: Record<string, unknown> | null;
  meta: RenderPayloadMeta | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ================================================================
// HOOK
// ================================================================

export function useDealCardRenderPayload(
  environment: string = "staging",
  dealId: string | null = null
): UseDealCardRenderPayloadResult {
  const [deal, setDeal] = useState<Record<string, unknown> | null>(null);
  const [meta, setMeta] = useState<RenderPayloadMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPayload = useCallback(async () => {
    if (!dealId) {
      setDeal(null);
      setMeta(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc(
        "get_deal_card_render_payload" as any,
        {
          p_environment: environment,
          p_deal_id: dealId,
        }
      );

      if (rpcError) throw new Error(rpcError.message);

      // RPC returns table rows — take first
      const row = Array.isArray(data) ? data[0] : data;

      if (!row) {
        setDeal(null);
        setMeta({ environment, deal_id: dealId, deal_type: null, dedupe_key_present: false, computed_value_source: "missing", missing_fields: [], source_view: "v_deals_live_enriched", updated_at: null, found: false });
        return;
      }

      const rpcMeta = row.meta as RenderPayloadMeta;
      const dealJson = row.deal_json as Record<string, unknown> | null;

      if (dealJson && rpcMeta?.found) {
        // CR317: RPC contract guarantees hoisted fields + canonical_deal_json.
        // No frontend hoisting needed — pass through directly.
        if (rpcMeta.hoisted !== true) {
          console.warn("[useDealCardRenderPayload] CR317: RPC returned hoisted !== true — contract violation. Deal may render with missing fields.", { dealId, meta: rpcMeta });
        }
        // CR434: Wrap dealJson with dealId so buildRenderableDeal can resolve id
        // (dealJson alone often has id=null in canonical_deal_json)
        const rowWithId = { ...dealJson, id: dealId, canonical_deal_json: dealJson };
        const normalized = buildRenderableDeal(rowWithId, "deal_card_editor");
        // CR447: Hydrate flyer snippet data so FlyerPreview can render images
        const hydrated = await hydrateDealFlyerIfMissing(normalized);
        setDeal(hydrated);
      } else {
        setDeal(null);
      }

      setMeta(rpcMeta ?? null);
    } catch (err) {
      console.error("[useDealCardRenderPayload] Error:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
      setDeal(null);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [environment, dealId]);

  useEffect(() => {
    fetchPayload();
  }, [fetchPayload]);

  return useMemo(() => ({
    deal,
    meta,
    loading,
    error,
    refetch: fetchPayload,
  }), [deal, meta, loading, error, fetchPayload]);
}
